import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type {
    MotionIntentType
} from "./timing";
import useMotionTiming, {
    type MotionThemeType
} from "./useMotionTiming";

export type PresenceConstantType = {
    isNativeDriverEnabled?: boolean;
    enterIntent?: MotionIntentType;
    customTheme?: MotionThemeType;
    exitIntent?: MotionIntentType;
    isInitialAnimated?: boolean;
    onExited?: () => void;
    isVisible: boolean;
};

export type PresenceType = {
    isReduceMotionEnabled: boolean;
    progress: Animated.Value;
    isRendered: boolean;
};

const usePresence = ({
    isNativeDriverEnabled = true,
    enterIntent = "listInsert",
    exitIntent = "listRemove",
    isInitialAnimated = false,
    customTheme,
    isVisible,
    onExited
}: PresenceConstantType): PresenceType => {
    const progress = useRef(new Animated.Value(isVisible && !isInitialAnimated ? 1 : 0)).current;

    const isMountedRef = useRef(false);

    const onExitedRef = useRef(onExited);

    const [
        isRendered,
        setIsRendered
    ] = useState(isVisible);

    const enterTiming = useMotionTiming(enterIntent, customTheme, "fade");

    const exitTiming = useMotionTiming(exitIntent, customTheme, "fade");

    onExitedRef.current = onExited;

    useEffect(() => {
        const isFirstRun = !isMountedRef.current;

        isMountedRef.current = true;

        if(isFirstRun && (!isVisible || !isInitialAnimated)) {
            return;
        }

        if(isVisible) {
            setIsRendered(true);
        }

        const timing = isVisible ? enterTiming : exitTiming;

        const animation = Animated.timing(progress, {
            useNativeDriver: isNativeDriverEnabled && Platform.OS !== "web",
            toValue: isVisible ? 1 : 0,
            duration: timing.duration,
            easing: timing.easing
        });

        animation.start(({
            finished
        }) => {
            if(!finished || isVisible) {
                return;
            }

            setIsRendered(false);

            onExitedRef.current?.();
        });

        return () => {
            animation.stop();
        };
    }, [
        isVisible
    ]);

    return {
        isReduceMotionEnabled: enterTiming.isReduceMotionEnabled,
        isRendered: isRendered || isVisible,
        progress
    };
};
export default usePresence;
